import type { Stream } from "./types";

/**
 * The Hub's two derived rails.
 *
 * Extracted from `HubPage` so the behaviour can be pinned by tests — these
 * encode a product decision rather than a mechanical transform, and the
 * "Live now" definition in particular is one the operator was asked to
 * confirm. A test is a more precise description of it than prose.
 */

export const HUB_RAIL_LIMIT = 12;

/**
 * Channels for "Live now".
 *
 * Favourited live channels first — the closest available signal for "the
 * channels this person actually watches". Only when there are none does it
 * fall back to catalogue order, which is the order Live TV itself shows, so
 * the rail is never arbitrary relative to the rest of the app.
 *
 * EPG-capable channels only: the programme line is the whole point of the
 * rail, and without one the card is just a Favourites tile in disguise.
 */
export function pickLiveNow(
  liveStreams: Stream[] | undefined,
  favouriteLiveIds: Iterable<string>,
  limit: number = HUB_RAIL_LIMIT,
): Stream[] {
  const all = liveStreams ?? [];
  if (!all.length) return [];
  const withEpg = all.filter((s) => !!s.epg_channel_id);
  const favIds = new Set([...favouriteLiveIds].map(String));
  const favourite = withEpg.filter((s) => favIds.has(String(s.id)));
  return (favourite.length ? favourite : withEpg).slice(0, limit);
}

/**
 * Films for "Recently added", newest first.
 *
 * `added` is a unix timestamp the panel supplies and the catalogue endpoint
 * already surfaces; `browse.py` sorts on the same field. Entries without it
 * are dropped rather than sorted as 0, which would park them at the end and
 * pad the rail with the oldest possible content.
 */
export function pickRecentlyAdded(
  vodStreams: Stream[] | undefined,
  limit: number = HUB_RAIL_LIMIT,
): Stream[] {
  const all = vodStreams ?? [];
  if (!all.length) return [];
  return [...all]
    .filter((s) => !!s.added)
    .sort((a, b) => Number(b.added || 0) - Number(a.added || 0))
    .slice(0, limit);
}
